// src/dnd/emitter_tool/components/panels/GravityPanel.jsx
import React, { useEffect, useState } from "react";

/** Notify the runtime whenever gravity-related values change. */
function dispatchParticlesPatch(patch) {
  try {
    const detail = {
      // Force parent scope so gravity applies to the main emitter even if a child is selected
      scopeType: "parent",
      scopeId: "parent",
      ...patch,
    };
    window.dispatchEvent(new CustomEvent("EmitterTool:ParticlesConfig", { detail }));
    console.debug("[GravityPanel→Runtime] Patch sent:", detail);
  } catch {}
}

/** Persisted open/closed state key */
const OPEN_KEY = "EmitterTool:GravityPanelOpenV2";

/** Simple collapsible wrapper with persisted state (no overflow) */
function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          gap: 8,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {title}
        </span>
        <span style={{ opacity: 0.75, flex: "0 0 auto" }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div style={{ padding: 10 }}>{children}</div>}
    </div>
  );
}

const labelStyle = { fontSize: 12, opacity: 0.85, overflowWrap: "anywhere" };

/* utils */
function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}
function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Full-width centred slider −10 … 0 … +10 with numeric input */
function CenteredSlider({
  value,
  onChange,
  min = -10,
  max = 10,
  step = 0.1,
  disabled = false,
  label = "Gravity speed",
}) {
  const [txt, setTxt] = useState(Number.isFinite(value) ? String(value) : "0");
  useEffect(() => {
    const s = String(Number.isFinite(value) ? value : 0);
    if (s !== txt) setTxt(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (raw) => {
    const val = clamp(toNum(raw, value ?? 0), min, max);
    onChange?.(val);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, opacity: disabled ? 0.5 : 1 }}>
      <div style={{ ...labelStyle, fontWeight: 600 }}>{label}</div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 90px",
          gap: 8,
          alignItems: "center",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              opacity: disabled ? 0.4 : 0.7,
              marginBottom: 2,
              padding: "0 2px",
            }}
          >
            <span>Inward ←</span>
            <span>0</span>
            <span>→ Outward</span>
          </div>

          <input
            type="range"
            value={Number.isFinite(value) ? value : 0}
            onChange={(e) => onChange?.(toNum(e.target.value, value ?? 0))}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            style={{ width: "100%" }}
          />
        </div>

        <input
          type="number"
          value={txt}
          onChange={(e) => setTxt(e.target.value)}
          onBlur={() => commit(txt)}
          onKeyDown={(e) => { if (e.key === "Enter") commit(txt); }}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          style={{
            background: "#0b0d12",
            color: "white",
            border: "1px solid #374151",
            borderRadius: 8,
            padding: "6px 8px",
            fontSize: 12,
            width: 90,
            boxSizing: "border-box",
          }}
        />
      </div>
    </div>
  );
}

function LingerDial({
  label = "Linger",
  value,
  onChange,
  min = 0,
  max = 3,
  step = 0.05,
  disabled = false,
  suffix = "s",
}) {
  const [txt, setTxt] = useState(Number.isFinite(value) ? String(value) : String(min));
  useEffect(() => {
    const s = String(Number.isFinite(value) ? value : min);
    if (s !== txt) setTxt(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max]);

  const commit = (raw) => onChange?.(clamp(toNum(raw, value ?? min), min, max));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, opacity: disabled ? 0.5 : 1 }}>
      <div style={{ ...labelStyle, fontWeight: 600 }}>{label}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 8, alignItems: "center" }}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={Number.isFinite(value) ? value : min}
          onChange={(e) => onChange?.(toNum(e.target.value, value ?? min))}
          disabled={disabled}
          style={{ width: "100%" }}
        />
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="number"
            value={txt}
            onChange={(e) => setTxt(e.target.value)}
            onBlur={() => commit(txt)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(txt); }}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            style={{
              background: "#0b0d12",
              color: "white",
              border: "1px solid #374151",
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              width: 90,
              boxSizing: "border-box",
            }}
          />
          <div style={{ fontSize: 12, opacity: 0.8 }}>{suffix}</div>
        </div>
      </div>
    </div>
  );
}

export default function GravityPanel({
  effectSettings = {},
  setEffectSettings,
  title = "Gravity",
  defaultOpen = false,
}) {
  const setES = (patch) => {
    if (typeof setEffectSettings === "function") {
      setEffectSettings((cur) => {
        const base = cur || {};
        const next = typeof patch === "function" ? patch(base) : patch;
        const out = { ...base, ...next };
        const gravKeys = ["gravityEnabled","gravitySpeed","gravityLingerS"];
        if (Object.keys(next).some(k => gravKeys.includes(k))) {
          const payload = {};
          for (const k of gravKeys) if (k in next) payload[k] = next[k];
          if (Object.keys(payload).length) dispatchParticlesPatch(payload);
        }
        return out;
      });
    }
  };

  const {
    gravityEnabled = false,
    gravitySpeed = 0,
    gravityLingerS = 0,
  } = effectSettings || {};

  const inwardActive = gravityEnabled && Number(gravitySpeed) < 0;

  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="checkbox"
            checked={!!gravityEnabled}
            onChange={(e) => setES({ gravityEnabled: e.target.checked })}
          />
          <span style={labelStyle}>Enable radial gravity</span>
        </label>
      </div>

      <div style={{ marginTop: 12 }}>
        <CenteredSlider
          label="Gravity speed (m/s)"
          value={Number(gravitySpeed)}
          onChange={(v) => setES({ gravitySpeed: clamp(v, -10, 10) })}
          min={-10}
          max={10}
          step={0.1}
          disabled={!gravityEnabled}
        />

        <div style={{ marginTop: 10 }}>
          <LingerDial
            label="Linger after stop"
            value={gravityLingerS}
            onChange={(v) => setES({ gravityLingerS: clamp(v, 0, 3) })}
            min={0}
            max={3}
            step={0.05}
            disabled={!inwardActive}
            suffix="s"
          />
        </div>

        <div style={{ fontSize: 12, opacity: 0.75, marginTop: 12, lineHeight: 1.4 }}>
          <div>
            <strong>Negative</strong>: pull inward (edge → centre → die).
          </div>
          <div>
            <strong>Positive</strong>: push outward (from centre toward boundary).
          </div>
          <div>
            <strong>Zero</strong>: no gravity movement.
          </div>
        </div>
      </div>
    </CollapsibleBox>
  );
}

export { GravityPanel };
